import { useParams, useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect, useRef } from 'react';
import { Target, Layers, Lightbulb, ImageIcon, HelpCircle, Quote as QuoteIcon, Loader2 } from 'lucide-react';
import FullPageLoader from '@/components/atoms/Loader/FullPageLoader';
import PaperAirplaneIcon from '@/components/icons/PaperAirplaneIcon';
import { Button } from '@/components/ui/button';
import { EProjectStatus } from '@/enums';
import CompactStats from './overview_content/CompactStats';
import {
  translateNoSummaryData,
  translateSummary,
  translateQuestions,
  translateNoQuestionsData,
  translateKeyThemes,
  translateNoKeyThemesData,
  translateInterestingQuotes,
  translateNoQuotesData,
  translateOpportunities,
  translateNoOpportunitiesData,
  translateVoiceOfTheCustomer,
  translateKeyFindings,
  translateNotableVisuals,
  translateRecommendations,
} from './project_results/resultsPageTranslations';
import QuestionAnswerAccordion from '@/components/projects/single-project/research-design/components/QuestionAnswerAccordion';
import InterestingQuotes from '@/components/QuestionItem/InterestingQuotes';
import useTranslation from '@/hooks/useTranslation';
import { OverviewProps, QuestionSummary } from '@/types';
import Summary from '@/components/projects/single-project/project-summary/ProjectSummary';
import ProjectQuestionInsights from '@/components/QuestionItem/ProjectQuestionInsights';
import QuotaTrackingWidget from '@/components/projects/single-project/overview/QuotaTrackingWidget';
import { resolveTranslation } from '@/utils/resolveTranslation';
import { useGetOverviewData, useGetScreeningStats } from '@/reactQuery/get';
import { useGetNotableVisuals } from '@/reactQuery/notableVisuals';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import AnalysisSection from '@/components/analysis/AnalysisSection';
import VoiceQuotes from '@/components/QuestionItem/VoiceQuotes';
import NotableVisualsGrid from '@/components/analysis/NotableVisualsGrid';
import SectionNav from '@/components/analysis/SectionNav';
import PartialAnalysisBanner from '@/components/analysis/PartialAnalysisBanner';
import SampleProfileCard from './overview_content/SampleProfileCard';
import Statistics from './overview_content/Statistics';
import ScreeningInsights from './overview_content/ScreeningInsights';

function Overview({
  isPendingReRun,
  analysisLanguage,
  newConversationsCount,
  onReRun,
  isRerunLoading,
  newConversationsState,
  rerunFilter,
  isLoadingBeforeApplyFilter,
  analysisProgress,
}: OverviewProps) {
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get('filter_id');
  const { lang } = useTranslation();
  const { currentProject: singleProjectData } = useCurrentProject();

  const { data: overviewData, isLoading: isOverviewLoading } = useGetOverviewData(
    filterId,
    projectId,
  );
  // Fetch unfiltered stats when a filter is active, to show "N of M interviews"
  const { data: unfilteredOverviewData } = useGetOverviewData(
    null,
    filterId ? projectId : undefined, // only fetch when filter is active
  );
  const totalCompletedInterviews = unfilteredOverviewData?.statistics?.completed_interviews;
  const { data: notableVisuals = [] } = useGetNotableVisuals(projectId, filterId);
  const hasScreeningQuestions = (singleProjectData?.screening_questions?.length ?? 0) > 0;
  const { data: screeningStats } = useGetScreeningStats(hasScreeningQuestions ? projectId : undefined);

  const questionSummary: QuestionSummary[] = overviewData?.question_summaries ?? [];
  const sortedQuestions = questionSummary?.every((item) => typeof item.index === 'number')
    ? questionSummary?.sort((a, b) => a.index - b.index)
    : questionSummary;

  const questionMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const q of questionSummary) {
      if (q.id) map[q.id] = q.title;
    }
    return map;
  }, [questionSummary]);

  // Check if there's any analysis data at all
  const noEnoughInterviewData =
    overviewData?.statistics === null &&
    overviewData?.summary?.length === 0 &&
    overviewData?.themes?.length === 0 &&
    overviewData?.quotes?.length === 0 &&
    overviewData?.opportunities?.length === 0 &&
    (!overviewData?.cross_question_patterns || overviewData.cross_question_patterns.length === 0) &&
    (!overviewData?.voice_quotes || overviewData.voice_quotes.length === 0);

  const isLoading = isPendingReRun || isOverviewLoading;
  const isPendingReRunOrReRunLoading = isPendingReRun || isRerunLoading;
  const isProjectCompleted = singleProjectData?.status === EProjectStatus.COMPLETED;
  const isApplyingFilter = (rerunFilter || isRerunLoading) && !!filterId;
  const isRunning = (!filterId || (filterId && isRerunLoading)) && !rerunFilter;
  let messageKey = '';
  if (isApplyingFilter) {
    messageKey = 'msg.applyingFilter';
  } else if (isRunning) {
    messageKey = 'msg.running';
  }
  const noInterviewResults =
    isProjectCompleted && !!filterId && !overviewData?.statistics && !rerunFilter;

  // Merge key findings from new-schema (cross_question_patterns) and old-schema (themes)
  const keyFindings = [
    ...(overviewData?.cross_question_patterns || []),
    ...(overviewData?.themes || []),
  ];

  // Merge recommendations from new-schema and old-schema (opportunities)
  const recommendations = [
    ...(overviewData?.recommendations || []),
    ...(overviewData?.opportunities || []),
  ];

  const hasQuotesData = !!overviewData?.quotes?.length;
  const hasQuestionSummaries = questionSummary.length > 0;

  // Build section nav items dynamically based on available data
  const sectionNav = useMemo(() => {
    const items: Array<{ id: string; label: string }> = [];
    items.push({ id: 'section-summary', label: resolveTranslation(translateSummary, analysisLanguage) });
    if (keyFindings.length > 0)
      items.push({ id: 'section-key-findings', label: resolveTranslation(translateKeyFindings, analysisLanguage) });
    if (overviewData?.voice_quotes?.length)
      items.push({ id: 'section-voice-of-customer', label: resolveTranslation(translateVoiceOfTheCustomer, analysisLanguage) });
    if (hasQuotesData && (!overviewData?.voice_quotes || overviewData.voice_quotes.length === 0))
      items.push({ id: 'section-quotes', label: resolveTranslation(translateInterestingQuotes, analysisLanguage) });
    if (notableVisuals.length > 0)
      items.push({ id: 'section-notable-visuals', label: resolveTranslation(translateNotableVisuals, analysisLanguage) });
    if (recommendations.length > 0)
      items.push({ id: 'section-recommendations', label: resolveTranslation(translateRecommendations, analysisLanguage) });
    if (hasQuestionSummaries)
      items.push({ id: 'section-questions', label: resolveTranslation(translateQuestions, analysisLanguage) });
    return items;
  }, [keyFindings, overviewData, hasQuotesData, notableVisuals, recommendations, hasQuestionSummaries, analysisLanguage]);

  // Track which section is currently in view via IntersectionObserver.
  // We maintain a Set of all currently-visible section IDs and pick the
  // first one (in document order) as the active section.
  const [activeSectionId, setActiveSectionId] = useState('section-summary');
  const visibleSectionsRef = useRef(new Set<string>());
  useEffect(() => {
    const ids = sectionNav.map((s) => s.id);
    const elements = ids.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (elements.length === 0) return;
    visibleSectionsRef.current.clear();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleSectionsRef.current.add(entry.target.id);
          } else {
            visibleSectionsRef.current.delete(entry.target.id);
          }
        }
        // Pick the first visible section in document order
        const firstVisible = ids.find((id) => visibleSectionsRef.current.has(id));
        if (firstVisible) {
          setActiveSectionId(firstVisible);
        }
      },
      { rootMargin: '-130px 0px -40% 0px', threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sectionNav]);

  if (isOverviewLoading || isLoadingBeforeApplyFilter) return <FullPageLoader />;

  // No data — show appropriate message based on context
  if (noEnoughInterviewData) {
    // Completed project with filter — analysis auto-runs on creation, no manual re-run
    if (isProjectCompleted && filterId) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full min-h-[65vh] text-center gap-3">
          {isRerunLoading ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm text-neutral-500">Analysis is running for this filter...</p>
            </>
          ) : (
            <>
              <p className="font-medium text-neutral-700">{lang.get('msg.noInterviewsThatMatchThisFilter')}</p>
              <p className="text-sm text-neutral-500">No matching interviews were found, or the analysis has not completed yet.</p>
            </>
          )}
        </div>
      );
    }

    // Pre-analysis — show full-width stats, screening, quotas, and run button
    return (
      <div className="flex flex-col w-full gap-5">
        <Statistics data={overviewData?.statistics} isLoading={isLoading} />

        {hasScreeningQuestions && projectId && (
          <ScreeningInsights projectId={projectId} />
        )}

        {singleProjectData?.screening_questions?.some((q) => q.quotas_enabled) && (
          <QuotaTrackingWidget />
        )}

        {/* Run analysis button or no-conversations alert */}
        <div className="flex justify-center py-4">
          {isRerunLoading ? (
            <span className="flex items-center gap-2 text-sm text-neutral-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              {lang.get(messageKey)}
            </span>
          ) : newConversationsCount > 0 ? (
            <Button
              variant="default"
              disabled={isPendingReRunOrReRunLoading}
              className="relative flex items-center gap-2 font-bold px-7"
              onClick={() => onReRun()}
            >
              {isPendingReRunOrReRunLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <PaperAirplaneIcon className="size-4" />
              )}
              {isPendingReRunOrReRunLoading ? 'Running...' : 'Run'}
              <span className="absolute flex items-center justify-center h-5 px-1.5 text-xs text-white bg-red-600 rounded-full w-fit -top-1 -right-1.5">
                {newConversationsCount}
              </span>
            </Button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-neutral-200 bg-neutral-50 text-sm text-neutral-600">
              <HelpCircle className="h-4 w-4 text-neutral-400 shrink-0" />
              {lang.get('msg.noConversationsYet')}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full gap-5">
      {/* Partial-failure banner — shown when the hierarchical pipeline
          finished with one or more questions that couldn't be analysed
          even after the inline retry phase. State is still DONE; this
          is data-level partial success, not a system error. */}
      {!isRerunLoading &&
        overviewData?.failed_questions &&
        overviewData.failed_questions.length > 0 && (
          <PartialAnalysisBanner
            failedQuestions={overviewData.failed_questions}
            totalQuestionCount={questionSummary.length || overviewData.failed_questions.length}
          />
        )}
      {/* Analysis in progress banner */}
      {isRerunLoading && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg text-sm text-primary">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <span>
            {analysisProgress
              ? analysisProgress.step === 'overall'
                ? 'Generating overview — results will refresh automatically.'
                : analysisProgress.step === 'batches'
                  ? `Processing batch ${analysisProgress.completed}/${analysisProgress.total} — results will refresh automatically.`
                  : analysisProgress.step === 'synthesis'
                    ? `Synthesizing question ${analysisProgress.completed}/${analysisProgress.total} — results will refresh automatically.`
                    : `Analyzing question ${analysisProgress.completed}/${analysisProgress.total} — results will refresh automatically.`
              : 'Analysis is updating — results will refresh automatically.'}
          </span>
        </div>
      )}

      {/* Grid: main content + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        {/* Main column */}
        <div className="flex flex-col gap-5 min-w-0">
          {/* Summary — always shown */}
          <Summary
            id="section-summary"
            title={resolveTranslation(translateSummary, analysisLanguage)}
            marginBottom="!mb-0"
            summary={overviewData?.summary ?? ''}
            isLoading={isLoading}
            noSummarydata={resolveTranslation(translateNoSummaryData, analysisLanguage)}
            voiceQuotes={overviewData?.voice_quotes}
          />

          {/* Key Findings (cross_question_patterns + themes) */}
          {keyFindings.length > 0 && (
            <ProjectQuestionInsights
              id="section-key-findings"
              title={resolveTranslation(translateKeyFindings, analysisLanguage)}
              data={keyFindings}
              isLoading={isLoading}
              noDataText={resolveTranslation(translateNoKeyThemesData, analysisLanguage)}
              icon={<Layers className="h-5 w-5 text-primary" />}
              voiceQuotes={overviewData?.voice_quotes}
            />
          )}

          {/* Voice of the Customer */}
          {overviewData?.voice_quotes && overviewData.voice_quotes.length > 0 && (
            <AnalysisSection
              id="section-voice-of-customer"
              title={resolveTranslation(translateVoiceOfTheCustomer, analysisLanguage)}
              icon={<QuoteIcon className="h-5 w-5 text-chat-primary" />}
              fullWidth
            >
              <VoiceQuotes
                quotes={overviewData.voice_quotes}
                isLoading={isLoading}
                showQuestionLabel
                questionMap={questionMap}
              />
            </AnalysisSection>
          )}

          {/* Interesting Quotes (old-schema fallback — shows when voice_quotes absent) */}
          {hasQuotesData && (!overviewData?.voice_quotes || overviewData.voice_quotes.length === 0) && (
            <AnalysisSection
              id="section-quotes"
              title={resolveTranslation(translateInterestingQuotes, analysisLanguage)}
              hasData={hasQuotesData}
              noDataText={resolveTranslation(translateNoQuotesData, analysisLanguage)}
              fullWidth
            >
              <InterestingQuotes
                analysisLanguage={analysisLanguage}
                data={overviewData?.quotes}
                isLoading={isLoading}
              />
            </AnalysisSection>
          )}

          {/* Notable Visuals */}
          {notableVisuals.length > 0 && (
            <AnalysisSection
              id="section-notable-visuals"
              title={resolveTranslation(translateNotableVisuals, analysisLanguage)}
              icon={<ImageIcon className="h-5 w-5 text-secondary" />}
              fullWidth
            >
              <NotableVisualsGrid visuals={notableVisuals} />
            </AnalysisSection>
          )}

          {/* Recommendations (recommendations + opportunities) */}
          {recommendations.length > 0 && (
            <ProjectQuestionInsights
              id="section-recommendations"
              title={resolveTranslation(translateRecommendations, analysisLanguage)}
              data={recommendations}
              isLoading={isLoading}
              noDataText={resolveTranslation(translateNoOpportunitiesData, analysisLanguage)}
              icon={<Target className="h-5 w-5 text-primary" />}
              voiceQuotes={overviewData?.voice_quotes}
            />
          )}

          {/* Question Summaries */}
          {hasQuestionSummaries && (
            <AnalysisSection
              id="section-questions"
              title={resolveTranslation(translateQuestions, analysisLanguage)}
              icon={<HelpCircle className="h-5 w-5 text-primary" />}
              fullWidth
              hasData={hasQuestionSummaries}
              noDataText={resolveTranslation(translateNoQuestionsData, analysisLanguage)}
            >
              <QuestionAnswerAccordion data={sortedQuestions} showExpandAll voiceQuotes={overviewData?.voice_quotes} />
            </AnalysisSection>
          )}
        </div>

        {/* Sidebar — sticks once its bottom reaches the viewport bottom,
            so stats show first and SectionNav reveals as you scroll. */}
        <div className="lg:sticky lg:bottom-8 lg:self-end flex flex-col gap-4">
          <CompactStats
            data={overviewData?.statistics}
            isLoading={isLoading}
            screeningData={screeningStats}
            isFiltered={!!filterId}
            totalCompletedInterviews={totalCompletedInterviews}
          />
          {overviewData?.sample_profile && (
            <SampleProfileCard sampleProfile={overviewData.sample_profile} />
          )}
          {singleProjectData?.screening_questions?.some((q) => q.quotas_enabled) && (
            <QuotaTrackingWidget />
          )}
          <SectionNav
            sections={sectionNav}
            activeId={activeSectionId}
          />
        </div>
      </div>
    </div>
  );
}

export default Overview;
